import React, { useState, useRef, useEffect } from 'react';
import { IoIosArrowDown } from 'react-icons/io';

const normalizeDefaultOpen = (defaultOpen) =>
    (Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen]).filter(Boolean);

const CustomAccordionWrapper = ({ children, multiple = false, defaultOpen = [] }) => {
    const defaultOpenKey = JSON.stringify(normalizeDefaultOpen(defaultOpen));
    const defaultOpenArray = JSON.parse(defaultOpenKey);
    const [openItems, setOpenItems] = useState(defaultOpenArray);

    useEffect(() => {
        setOpenItems(JSON.parse(defaultOpenKey));
    }, [defaultOpenKey]);

    const toggleItem = (id) => {
        setOpenItems(prev => {
            if (prev.includes(id)) {
                return prev.filter(itemId => itemId !== id);
            }
            return multiple ? [...prev, id] : [id];
        });
    };

    return React.Children.map(children, child => {
        if (!React.isValidElement(child) || !child.props?.id) {
            return child;
        }
        return React.cloneElement(child, {
            isOpen: openItems.includes(child.props.id),
            toggle: () => toggleItem(child.props.id)
        });
    });
};

const CustomAccordionItem = ({ id, children, isOpen, toggle, className = '', autoScroll = false }) => {
    const itemRef = useRef(null);

    useEffect(() => {
        if (autoScroll && isOpen && itemRef.current) {
            // Delay slightly to allow the accordion opening animation to start/complete
            setTimeout(() => {
                itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        }
    }, [isOpen, autoScroll]);

    return (
        <div ref={itemRef} className={`accordion-item ${isOpen ? 'open' : ''} ${className}`}>
            {React.Children.map(children, child => {
                if (child.type === CustomAccordionHeader) {
                    return React.cloneElement(child, { isOpen, toggle });
                }
                if (child.type === CustomAccordionBody) {
                    return React.cloneElement(child, { isOpen });
                }
                return child;
            })}
        </div>
    );
};

const CustomAccordionHeader = ({
    children,
    isOpen,
    toggle,
    className = '',
    arrowtext = "",
    toggleStyle = "arrow",
}) => {
    return (
        <div
            className={`accordion-header ${className}`}
            onClick={toggle}
            style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                {typeof children === 'function' ? children({ isOpen, toggle }) : children}
            </div>
            <div className='d-flex align-items-center gap-3'>
                {arrowtext ? <p className='fs12 fw-400 mg-text-gray1'>{arrowtext}</p> : null}
                {toggleStyle === "plus-minus" ? (
                    <span className="accordion-toggle-icon" aria-hidden="true">
                        {isOpen ? "−" : "+"}
                    </span>
                ) : (
                    <IoIosArrowDown
                        className="accordion-arrow"
                        style={{
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const CustomAccordionBody = ({ children, isOpen, className = '', scrollMaxHeight = null }) => {
    const bodyRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);

    useEffect(() => {
        if (bodyRef.current) {
            setContentHeight(bodyRef.current?.scrollHeight);
            const resizeObserver = new ResizeObserver(() => {
                setContentHeight(bodyRef.current?.scrollHeight);
            });
            resizeObserver.observe(bodyRef?.current);
            return () => resizeObserver.disconnect();
        }
    }, [children, isOpen]);

    const maxHeight = scrollMaxHeight ? Number(scrollMaxHeight) : null;
    const openHeight =
        maxHeight && contentHeight > maxHeight ? maxHeight : contentHeight;
    const isScrollable = Boolean(maxHeight && contentHeight > maxHeight);

    return (
        <div
            className={`accordion-body ${className} ${isOpen ? "isShow" : "isHide"} ${isScrollable ? "accordion-body--scrollable" : ""}`}
            ref={bodyRef}
            style={{
                height: isOpen ? `${openHeight}px` : '0px',
                overflow: isOpen && isScrollable ? 'auto' : 'hidden',
                transition: 'height 0.3s ease',
            }}
        >
            <div>{children}</div>
        </div >
    );
};

export {
    CustomAccordionWrapper,
    CustomAccordionItem,
    CustomAccordionHeader,
    CustomAccordionBody,
};